/**
 * Choropleth Style Manager
 *
 * Manages the separation between WHAT data is shown (metric, color scale)
 * and HOW it is shown (flat fill, 3D extrusion, bubbles, hexagons).
 *
 * Data metric is owned by query-router.js / LayerManager (unchanged).
 * Visualization mode is owned by this manager.
 *
 * Modes:
 *   flat     — MapLibre fill layer (existing behavior, baseline)
 *   extruded — MapLibre fill-extrusion layer (3D bars rising off globe)
 *   symbols  — Deck.gl ScatterplotLayer at country centroids (bubble map)
 *   hexbin   — Deck.gl H3HexagonLayer (equal-area hexagon grid)
 *
 * Color classification is delegated to ClassificationEngine which provides
 * 7-level log-quantile breaks with relative/absolute scale support.
 */
import { ScatterplotLayer } from '@deck.gl/layers'
import { latLngToCell } from 'h3-js'
import { H3HexagonLayer } from '@deck.gl/geo-layers'
import { computeCentroid } from '../utils/geo-utils.js'
import { classificationEngine, NULL_COLOR } from './classification-engine.js'

// CAMERA_OVERRIDES for countries with non-contiguous territory
const CAMERA_OVERRIDES = {
  USA: [-98.5, 39.5],
  RUS: [55, 60],
  FRA: [2.2, 46.2],
  NOR: [10, 62],
  DNK: [10, 56],
  NLD: [5.3, 52.1],
  GBR: [-2, 54],
  NZL: [173, -41],
  CHL: [-71, -35],
  PRT: [-8.5, 39.5],
  ECU: [-78.5, -1.5],
}

// Layer IDs
const FILL_LAYER = 'data-choropleth'
const EXTRUSION_LAYER = 'choropleth-extrusion'
const SYMBOLS_LAYER = 'choropleth-symbols'
const HEXBIN_LAYER = 'choropleth-hexbin'

// Alias chains for attribute name mismatches between
// Natural Earth properties and SQL-aliased DuckDB output columns.
const PROP_ALIASES = {
  pop_est: ['population', 'POP_EST'],
  POP_EST: ['population', 'pop_est'],
  population: ['pop_est', 'POP_EST'],
  gdp_md: ['GDP_MD', 'gdp', 'gdp_usd'],
  GDP_MD: ['gdp_md', 'gdp', 'gdp_usd'],
  name_en: ['name', 'NAME'],
  name: ['name_en', 'NAME'],
  pop_density: ['density_per_km2', 'pop_density_km2'],
}

/**
 * Get a property value case-insensitively, with alias fallback.
 */
function getProp(feature, key) {
  const p = feature.properties
  if (!p) return undefined
  if (p[key] !== undefined) return p[key]
  if (p[key.toLowerCase()] !== undefined) return p[key.toLowerCase()]
  if (p[key.toUpperCase()] !== undefined) return p[key.toUpperCase()]
  // Try known aliases
  const aliases = PROP_ALIASES[key] || PROP_ALIASES[key.toLowerCase()]
  if (aliases) {
    for (const alt of aliases) {
      if (p[alt] !== undefined) return p[alt]
    }
  }
  return undefined
}

/**
 * Convert RGBA array [r,g,b,a] to hex string #RRGGBB.
 */
function rgbaToHex(rgba) {
  return (
    '#' +
    [rgba[0], rgba[1], rgba[2]].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')
  )
}

/**
 * Convert hex color to RGBA array.
 */
function hexToRgba(hex) {
  const m = hex.replace('#', '').match(/.{2}/g)
  if (!m || m.length < 3) return [128, 128, 128, 200]
  return [parseInt(m[0], 16), parseInt(m[1], 16), parseInt(m[2], 16), 200]
}

export class ChoroplethStyleManager {
  /**
   * @param {object} globe - Globe instance (has .map, .deckOverlay, .addPolygonLayer, etc.)
   * @param {object} layerManager - LayerManager instance (for clearLayers, _deckLayers, etc.)
   */
  constructor(globe, layerManager) {
    this.globe = globe
    this.map = globe.map
    this.layerManager = layerManager
    this.currentMode = 'flat'
    this.currentData = null
    this.scaleMode = 'relative' // 'relative' | 'absolute'

    // Classification results (cached so mode switch doesn't reclassify)
    this._classifiedColors = null // Map<featureIndex, rgba>
    this._thresholds = null
    this._profile = null
    this._legendConfig = null

    // Callbacks
    this.onModeChange = null // (mode) => void — for UI button highlighting
    this.onLegendUpdate = null // (config) => void — for legend panel

    // Spatial Analysis Advisor hint (advisory only — currently used to
    // select the palette family: sequential, diverging, qualitative,
    // bivariate). Populated by query-router before render.
    this._advisorHint = null
  }

  /**
   * Store a Spatial Analysis Advisor hint for the next render. The style
   * manager uses the color_ramp_type field to pick the palette family.
   */
  setAdvisorHint(hint) {
    this._advisorHint = hint || null
    if (hint && hint.color_ramp_type) {
      console.debug(
        `[spatial-advisor] palette hint: ${hint.color_ramp_type} (method=${hint.method || 'unknown'})`
      )
    }
  }

  /**
   * Store choropleth data so mode switches can re-render without re-fetching.
   * Called by LayerManager.showChoropleth() after enrichment.
   *
   * @param {object} data - Choropleth data
   * @param {number[]} [precomputedBreaks] - Optional pre-computed classification breaks
   */
  setData(data, precomputedBreaks) {
    this.currentData = data
    this._classify(precomputedBreaks)
    this.render()
  }

  /**
   * Accept pre-classified data where _fill is already set by the caller.
   * Computes _extHeight for extrusion support and enables mode switching,
   * but does NOT re-run the classification engine.
   *
   * Use this for specialized choropleth methods (Fragile States, Agriculture,
   * Climate Zones, Freedom House, Division Choropleth) that compute their own
   * colors but still want extrusion + mode switcher support.
   *
   * @param {object} opts
   * @param {object} opts.enrichedGeojson - GeoJSON with _fill properties already set
   * @param {string} opts.attribute - numeric attribute for extrusion height (or null for categorical)
   * @param {object} [opts.linePaint] - MapLibre line paint
   * @param {object} [opts.legendConfig] - pre-built legend config to emit
   */
  setPreclassifiedData({ enrichedGeojson, attribute, linePaint, legendConfig }) {
    const MAX_HEIGHT = 1200000

    // Compute _extHeight from the numeric attribute (if available)
    if (attribute) {
      let maxValue = 0
      for (const f of enrichedGeojson.features) {
        const v = Number(getProp(f, attribute))
        if (!isNaN(v) && v > maxValue) maxValue = v
      }
      for (const f of enrichedGeojson.features) {
        const v = Number(getProp(f, attribute))
        if (!isNaN(v) && v > 0 && maxValue > 0) {
          f.properties._extHeight = Math.pow(v / maxValue, 0.4) * MAX_HEIGHT
        } else {
          f.properties._extHeight = f.properties._extHeight || 0
        }
      }
    } else {
      // Categorical: assign modest fixed height so extrusion mode shows something
      for (const f of enrichedGeojson.features) {
        if (!f.properties._extHeight) {
          f.properties._extHeight = 200000 // 200km default for categorical
        }
      }
    }

    // Store as currentData (same shape as setData expects)
    this.currentData = {
      enrichedGeojson,
      attribute: attribute || '_categorical',
      linePaint: linePaint || { 'line-color': 'rgba(255,255,255,0.5)', 'line-width': 0.8 },
    }

    // Emit the pre-built legend (if provided)
    if (legendConfig) {
      this._legendConfig = legendConfig
      this.onLegendUpdate?.(legendConfig)
    }

    this.render()
  }

  /**
   * Switch visualization mode. Data persists. No reclassification needed.
   * @param {'flat'|'extruded'|'symbols'|'hexbin'} mode
   */
  setMode(mode) {
    if (mode === this.currentMode) return
    const prevMode = this.currentMode
    this.currentMode = mode

    if (this.currentData) {
      this.render()
    }

    // Camera adjustments
    if (mode === 'extruded' && prevMode !== 'extruded') {
      this.map.easeTo({ pitch: 45, duration: 800 })
    } else if (prevMode === 'extruded' && mode !== 'extruded') {
      this.map.easeTo({ pitch: 0, duration: 800 })
    }

    this.onModeChange?.(mode)
  }

  /**
   * Switch between relative and absolute scale. Reclassifies and re-renders.
   * @param {'relative'|'absolute'} mode
   */
  setScaleMode(mode) {
    if (mode === this.scaleMode) return
    this.scaleMode = mode
    if (this.currentData) {
      this._classify()
      this.render()
    }
  }

  /**
   * Render the current data in the current mode.
   */
  render() {
    if (!this.currentData) return
    this._clearAllModes()

    switch (this.currentMode) {
      case 'flat':
        this._renderFlat()
        break
      case 'extruded':
        this._renderExtruded()
        break
      case 'symbols':
        this._renderSymbols()
        break
      case 'hexbin':
        this._renderHexbin()
        break
    }
  }

  /**
   * Clear all choropleth layers across all modes.
   */
  clear() {
    this.currentData = null
    this._classifiedColors = null
    this._thresholds = null
    this._profile = null
    this._legendConfig = null
    this._clearAllModes()
    this.onModeChange?.(null)
  }

  getCurrentMode() {
    return this.currentMode
  }

  hasData() {
    return this.currentData !== null
  }

  getCurrentLegend() {
    return this._legendConfig
  }

  // ── Classification ─────────────────────────────────────────

  /**
   * Run the classification engine on the current data.
   * Assigns _fill (hex color) and _extHeight to each feature.
   *
   * @param {number[]} [precomputedBreaks] - Optional pre-computed breaks to skip computeBreaks()
   */
  _classify(precomputedBreaks) {
    const { enrichedGeojson, attribute } = this.currentData
    const metricProp = this._findMetricProperty(enrichedGeojson, attribute)
    const metricKey = this._resolveMetricKey(attribute)
    const profile = classificationEngine.resolveProfile(metricKey)
    const adm = this.currentData.adm || 'ADM0'

    // Extract values for classification
    const values = []
    for (const f of enrichedGeojson.features) {
      const v = Number(getProp(f, metricProp))
      values.push(isNaN(v) ? null : v)
    }

    const validValues = values.filter((v) => v != null && !isNaN(v))

    // Use pre-computed breaks if provided (from catalog or DuckDB), else compute
    const thresholds =
      precomputedBreaks ||
      classificationEngine.computeBreaks(validValues, profile, this.scaleMode, null, adm, metricKey)
    if (precomputedBreaks) {
      console.log(
        `[ChoroplethStyleManager] Using pre-computed breaks: [${precomputedBreaks.map((b) => b.toFixed(1)).join(', ')}]`
      )
    }

    // Find max value for extrusion height scaling
    let maxValue = 0
    for (const v of validValues) {
      if (v > maxValue) maxValue = v
    }

    const MAX_HEIGHT = 1200000

    // Assign colors and heights to each feature
    for (let i = 0; i < enrichedGeojson.features.length; i++) {
      const f = enrichedGeojson.features[i]
      const v = values[i]
      const bracket = classificationEngine.classify(v, thresholds, profile)
      const color = classificationEngine.getColor(bracket, profile)

      f.properties._fill = rgbaToHex(color)
      f.properties._tierFill = rgbaToHex(color)
      f.properties._bracket = bracket

      // Extrusion height: power-curve within bracket system
      if (bracket > 0 && v > 0 && maxValue > 0) {
        f.properties._extHeight = Math.pow(v / maxValue, 0.4) * MAX_HEIGHT
      } else {
        f.properties._extHeight = 0
      }
    }

    this._thresholds = thresholds
    this._profile = profile

    // Build and emit legend
    this._legendConfig = classificationEngine.buildLegend(thresholds, profile, this.scaleMode, adm)
    this.onLegendUpdate?.(this._legendConfig)
  }

  /**
   * Map attribute names to metric profile keys.
   */
  _resolveMetricKey(attribute) {
    const lower = (attribute || '').toLowerCase()
    const aliasMap = {
      // Natural Earth basics
      population: 'population',
      pop_est: 'population',
      pop: 'population',
      pop_max: 'population',
      gdp_md: 'gdp_md',
      gdp: 'gdp_total',
      gdp_md_est: 'gdp_md',
      income_grp: 'income_grp',
      density_per_km2: 'density_per_km2',
      pop_density_km2: 'density_per_km2',
      pop_density: 'density_per_km2',
      density: 'density_per_km2',
      area_km2: 'area_km2',
      area: 'area_km2',
      pop_pct_national: 'pop_pct_national',
      hdi: 'hdi',
      life_expectancy: 'life_expectancy',
      co2_emissions: 'co2_emissions',
      population_growth: 'population_growth',
      // World Bank indicators (direct matches — keys match METRIC_PROFILES)
      pop_growth_pct: 'pop_growth_pct',
      urban_pct: 'urban_pct',
      gdp_usd: 'gdp_usd',
      gdp_per_capita: 'gdp_per_capita',
      gini_index: 'gini_index',
      unemployment_pct: 'unemployment_pct',
      literacy_rate: 'literacy_rate',
      primary_enrollment: 'primary_enrollment',
      secondary_enrollment: 'secondary_enrollment',
      infant_mortality: 'infant_mortality',
      physicians_per_1k: 'physicians_per_1k',
      health_spending_pct: 'health_spending_pct',
      forest_area_pct: 'forest_area_pct',
      co2_per_capita: 'co2_per_capita',
      renewable_energy_pct: 'renewable_energy_pct',
      electricity_access_pct: 'electricity_access_pct',
      internet_users_pct: 'internet_users_pct',
      mobile_subscriptions: 'mobile_subscriptions',
      // Demographics (extended)
      birth_rate: 'birth_rate',
      death_rate: 'death_rate',
      fertility_rate: 'fertility_rate',
      age_dependency_ratio: 'age_dependency_ratio',
      pop_ages_0_14_pct: 'pop_ages_0_14_pct',
      pop_ages_65_plus_pct: 'pop_ages_65_plus_pct',
      net_migration: 'net_migration',
      adolescent_fertility: 'adolescent_fertility',
      pop_density_wb: 'pop_density_wb',
      // Economics (extended)
      gdp_growth_pct: 'gdp_growth_pct',
      gni_per_capita: 'gni_per_capita',
      poverty_headcount: 'poverty_headcount',
      unemployment_female_pct: 'unemployment_female_pct',
      inflation_pct: 'inflation_pct',
      trade_pct_gdp: 'trade_pct_gdp',
      exports_pct_gdp: 'exports_pct_gdp',
      imports_pct_gdp: 'imports_pct_gdp',
      fdi_pct_gdp: 'fdi_pct_gdp',
      govt_debt_pct_gdp: 'govt_debt_pct_gdp',
      current_account_pct: 'current_account_pct',
      labor_force_participation: 'labor_force_participation',
      // Economic structure
      agriculture_pct_gdp: 'agriculture_pct_gdp',
      industry_pct_gdp: 'industry_pct_gdp',
      services_pct_gdp: 'services_pct_gdp',
      employment_agriculture_pct: 'employment_agriculture_pct',
      employment_industry_pct: 'employment_industry_pct',
      employment_services_pct: 'employment_services_pct',
      // Education (extended)
      literacy_rate_female: 'literacy_rate_female',
      tertiary_enrollment: 'tertiary_enrollment',
      primary_completion: 'primary_completion',
      education_spending_pct: 'education_spending_pct',
      compulsory_education_yrs: 'compulsory_education_yrs',
      // Health (extended)
      life_expectancy_female: 'life_expectancy_female',
      life_expectancy_male: 'life_expectancy_male',
      under5_mortality: 'under5_mortality',
      maternal_mortality: 'maternal_mortality',
      hospital_beds_per_1k: 'hospital_beds_per_1k',
      clean_water_pct: 'clean_water_pct',
      basic_sanitation_pct: 'basic_sanitation_pct',
      measles_immunization_pct: 'measles_immunization_pct',
      tuberculosis_incidence: 'tuberculosis_incidence',
      obesity_prevalence_pct: 'obesity_prevalence_pct',
      obesity_prevalence: 'obesity_prevalence_pct', // alias
      // Environment (extended)
      arable_land_pct: 'arable_land_pct',
      agricultural_land_pct: 'agricultural_land_pct',
      pm25_air_pollution: 'pm25_air_pollution',
      protected_areas_pct: 'protected_areas_pct',
      marine_protected_pct: 'marine_protected_pct',
      // Energy & Infrastructure (extended)
      energy_use_per_capita: 'energy_use_per_capita',
      electricity_consumption_kwh: 'electricity_consumption_kwh',
      air_transport_departures: 'air_transport_departures',
      // Gender equity
      women_in_parliament_pct: 'women_in_parliament_pct',
      women_in_parliament: 'women_in_parliament_pct', // alias
      female_labor_force_pct: 'female_labor_force_pct',
      gender_parity_primary: 'gender_parity_primary',
      gender_parity_secondary: 'gender_parity_secondary',
      // Composite
      income_group_rank: 'income_group_rank',
    }
    return aliasMap[lower] || lower
  }

  // ── Private render methods ─────────────────────────────────

  /**
   * Mode A: Flat fill.
   */
  _renderFlat() {
    const { enrichedGeojson, linePaint } = this.currentData
    this.globe.addPolygonLayer(FILL_LAYER, enrichedGeojson, {
      fill: {
        'fill-color': ['coalesce', ['get', '_fill'], '#787878'],
        'fill-opacity': 0.75,
      },
      line: linePaint,
    })
  }

  /**
   * Mode B: Extruded 3D fill-extrusion on globe.
   */
  _renderExtruded() {
    const { enrichedGeojson, linePaint } = this.currentData

    // Flat fill underneath (low opacity so extrusions dominate)
    this.globe.addPolygonLayer(FILL_LAYER, enrichedGeojson, {
      fill: {
        'fill-color': ['coalesce', ['get', '_fill'], '#787878'],
        'fill-opacity': 0.15,
      },
      line: linePaint,
    })

    const sourceId = `${FILL_LAYER}-src`
    const extrusionId = `${EXTRUSION_LAYER}-fill`

    this.globe._exec(() => {
      try {
        if (this.map.getLayer(extrusionId)) this.map.removeLayer(extrusionId)
        this.map.addLayer({
          id: extrusionId,
          type: 'fill-extrusion',
          source: sourceId,
          paint: {
            'fill-extrusion-color': ['coalesce', ['get', '_fill'], '#787878'],
            'fill-extrusion-height': ['coalesce', ['get', '_extHeight'], 0],
            'fill-extrusion-base': 0,
            'fill-extrusion-opacity': 0.85,
          },
        })
      } catch (err) {
        console.error('[ChoroplethStyleManager] Extrusion layer failed:', err)
      }
    })
  }

  /**
   * Mode C: Proportional symbols (bubble map).
   */
  _renderSymbols() {
    const { enrichedGeojson, attribute } = this.currentData
    const features = enrichedGeojson.features
    const symbolData = []
    let maxValue = 0

    for (const f of features) {
      const numVal = Number(getProp(f, attribute))
      if (!numVal || numVal <= 0) continue
      if (numVal > maxValue) maxValue = numVal

      const iso3 =
        getProp(f, 'iso3') ||
        getProp(f, 'parent_iso3') ||
        getProp(f, 'ISO_A3') ||
        getProp(f, 'iso_a3') ||
        getProp(f, 'shapeGroup') ||
        ''
      const centroid = CAMERA_OVERRIDES[iso3] || computeCentroid(f.geometry)
      const fill = f.properties._fill
      const rgba = fill ? hexToRgba(fill) : [128, 128, 128, 200]

      symbolData.push({
        position: centroid,
        value: numVal,
        color: rgba,
        name:
          getProp(f, 'name_en') ||
          getProp(f, 'NAME') ||
          getProp(f, 'name') ||
          getProp(f, 'shapeName') ||
          '',
        iso: iso3,
        attribute,
      })
    }

    for (const d of symbolData) {
      d.radius = getSymbolRadius(d.value, maxValue)
    }

    const layer = new ScatterplotLayer({
      id: SYMBOLS_LAYER,
      data: symbolData,
      getPosition: (d) => d.position,
      getRadius: (d) => d.radius,
      getFillColor: (d) => d.color,
      getLineColor: [255, 255, 255, 180],
      lineWidthMinPixels: 1,
      stroked: true,
      filled: true,
      radiusUnits: 'meters',
      opacity: 0.8,
      pickable: true,
      autoHighlight: true,
      highlightColor: [255, 255, 255, 80],
    })

    this.layerManager._deckLayers.set(SYMBOLS_LAYER, layer)
    this.layerManager._updateDeckOverlay()
  }

  /**
   * Mode D: H3 hexbin.
   */
  _renderHexbin() {
    const { enrichedGeojson, attribute } = this.currentData
    const features = enrichedGeojson.features
    const bins = new Map()
    let maxValue = 0

    for (const f of features) {
      const val = Number(getProp(f, attribute))
      if (!val || val <= 0) continue

      const iso3 =
        getProp(f, 'iso3') ||
        getProp(f, 'parent_iso3') ||
        getProp(f, 'ISO_A3') ||
        getProp(f, 'iso_a3') ||
        getProp(f, 'shapeGroup') ||
        ''
      const centroid = CAMERA_OVERRIDES[iso3] || computeCentroid(f.geometry)
      const [lng, lat] = centroid
      const hexId = latLngToCell(lat, lng, 3)

      if (!bins.has(hexId)) {
        bins.set(hexId, { hexId, totalValue: 0, count: 0, names: [] })
      }
      const bin = bins.get(hexId)
      bin.totalValue += val
      bin.count++
      if (bin.names.length < 3)
        bin.names.push(getProp(f, 'name_en') || getProp(f, 'NAME') || getProp(f, 'name') || '')
    }

    const hexData = Array.from(bins.values())
    for (const d of hexData) {
      d.avgValue = d.totalValue / d.count
      if (d.totalValue > maxValue) maxValue = d.totalValue
    }

    // Classify hex bins using the engine
    const metricKey = this._resolveMetricKey(attribute)
    const profile = classificationEngine.resolveProfile(metricKey)
    const hexValues = hexData.map((d) => d.totalValue)
    const thresholds = classificationEngine.computeBreaks(hexValues, profile, 'relative')

    for (const d of hexData) {
      const bracket = classificationEngine.classify(d.totalValue, thresholds, profile)
      const color = classificationEngine.getColor(bracket, profile)
      d.color = [color[0], color[1], color[2], Math.round(180 + 60 * (bracket / 7))]
    }

    const layer = new H3HexagonLayer({
      id: HEXBIN_LAYER,
      data: hexData,
      pickable: true,
      filled: true,
      extruded: false,
      stroked: true,
      lineWidthMinPixels: 1,
      opacity: 0.75,
      getHexagon: (d) => d.hexId,
      getFillColor: (d) => d.color,
      updateTriggers: { getFillColor: [maxValue, this.scaleMode] },
    })

    this.layerManager._deckLayers.set(HEXBIN_LAYER, layer)
    this.layerManager._updateDeckOverlay()
  }

  // ── Helpers ────────────────────────────────────────────────

  _clearAllModes() {
    try {
      const extId = `${EXTRUSION_LAYER}-fill`
      if (this.map.getLayer(extId)) this.map.removeLayer(extId)
    } catch (e) {
      /* may not exist */
    }

    this.globe.removePolygonLayer(FILL_LAYER)
    this.layerManager._deckLayers.delete(SYMBOLS_LAYER)
    this.layerManager._deckLayers.delete(HEXBIN_LAYER)
    this.layerManager._updateDeckOverlay()
  }

  _findMetricProperty(geojson, attribute) {
    const f = geojson.features?.[0]
    if (!f) return attribute
    const p = f.properties
    if (p[attribute] !== undefined) return attribute
    if (p[attribute.toLowerCase()] !== undefined) return attribute.toLowerCase()
    if (p[attribute.toUpperCase()] !== undefined) return attribute.toUpperCase()
    const map = {
      POP_EST: 'population',
      pop_est: 'population',
      GDP_MD: 'gdp_md',
      INCOME_GRP: 'income_grp',
    }
    if (map[attribute] && p[map[attribute]] !== undefined) return map[attribute]
    return attribute
  }
}

/**
 * Logarithmic radius scaling for proportional symbols.
 */
function getSymbolRadius(value, maxValue) {
  if (!value || value <= 0) return 0
  const logScale = Math.log10(value) / Math.log10(Math.max(maxValue, 10))
  return 50000 + logScale * 1450000
}
